import { useState } from 'react';
import { Bloque, Pagina, Tabla } from '../componentes/Estructura';
import { Cargando, ConError, Vacio } from '../componentes/Estados';
import { DialogoMotivo } from '../componentes/DialogoMotivo';
import { dinero, laFechaHora } from '../componentes/formato';
import { useCargaReal } from '../dominio/carga';
import { ErrorApi } from '../dominio/clienteApi';
import { obtenerPlanSuscripcion, publicarPlanSuscripcion } from '../dominio/planSuscripcionApi';
import type {
  IntervaloSuscripcion,
  PlanSuscripcionVigente,
  PublicarPlanSuscripcion,
} from '../dominio/planSuscripcionApi';

/**
 * Plan de suscripción — el monto, el intervalo y la moneda que se le cobra al conductor cada mes.
 * Antes era `SUSCRIPCION_MONTO_CENTIMOS`, una variable de entorno fija; esta pantalla lo hace
 * parametrizable y versionado, mismo patrón que `Dispatch.tsx` (ver `parametrosDispatchApi.ts`
 * para el contrato completo).
 *
 * 🔴 **`origen === 'SIN_CONFIGURAR'` NO es el mismo caso que el `FALLBACK` de dispatch o precio.**
 * Ahí siempre hay un número de arranque razonable que copiar al formulario (dispatch) o un modelo
 * que no compromete nada (precio). Acá no: es dinero real, y no hay un monto por defecto que
 * inventar sin que sea una decisión de negocio disfrazada de dato técnico. Mientras dure, ningún
 * conductor puede iniciar su cobro mensual — el aviso de abajo lo dice así, no como una falla de
 * red.
 */

/** Cómo se le muestra cada intervalo al operador. */
const ETIQUETA_INTERVALO: Record<IntervaloSuscripcion, string> = {
  MENSUAL: 'Mensual',
  TRIMESTRAL: 'Trimestral',
  SEMESTRAL: 'Semestral',
  ANUAL: 'Anual',
};

const INTERVALOS_TODOS: readonly IntervaloSuscripcion[] = [
  'MENSUAL',
  'TRIMESTRAL',
  'SEMESTRAL',
  'ANUAL',
];

/** Nombre y moneda por defecto cuando no hay NADA que copiar — ver `valoresPorDefecto`. */
const NOMBRE_POR_DEFECTO = 'Apata — tarifa plana mensual';
const MONEDA_POR_DEFECTO = 'PEN';

/** El mensaje que ve el operador cuando la publicación falla. `ErrorApi` ya trae el motivo del backend. */
const mensajeDeFalla = (error: unknown): string =>
  error instanceof ErrorApi ? error.message : 'Ocurrió un error inesperado. Probá de nuevo.';

/**
 * Monto en céntimos formateado en su moneda, tolerante a una moneda que no sea un código ISO
 * real. `Intl.NumberFormat` **lanza `RangeError`** con un `currency` que no tenga 3 letras
 * reconocidas — y `moneda` acá es un `<input>` de texto libre, así que una fila ya publicada (o
 * un borrador a mitad de escribir) puede traer cualquier cosa. Sin este `catch`, una moneda rara
 * no rompe una celda: rompe el render de toda la pantalla. Mismo patrón que `importe()` en
 * `Tarifario.tsx`.
 */
function formatoMonto(centimos: number, moneda: string): string {
  if (!Number.isFinite(centimos)) return '—';
  const soles = centimos / 100;
  try {
    return dinero(soles, moneda);
  } catch {
    return `${moneda} ${soles.toFixed(2)}`;
  }
}

/** Los cuatro campos editables. El backend publica un SNAPSHOT COMPLETO, no un parche. */
export interface EditablesPlan {
  montoCentimos: number;
  intervalo: IntervaloSuscripcion;
  moneda: string;
  nombre: string;
}

/**
 * El borrador inicial.
 *
 * 🔴 **La rama `SIN_CONFIGURAR` es la parte no trivial de esta función, y es donde un bug se
 * metería fácil.** Leer `efectivos.montoCentimos` sin comprobar primero el discriminante
 * `origen` rompería con `undefined` — `PlanSuscripcionVigente` en ese lado de la unión NO TIENE
 * ese campo, a propósito (ver la cabecera de `planSuscripcionApi.ts`). `montoCentimos` arranca en
 * `NaN` (el `<input type="number">` lo muestra vacío) para que el operador tenga que ESCRIBIR el
 * monto real, en vez de heredar un valor inventado por el cliente — que es exactamente la clase
 * de número que este archivo existe para no inventar. `intervalo`/`moneda`/`nombre` sí llevan un
 * default: son los MISMOS que el backend aplicaría solo si se omiten del `POST` (ver el DTO en
 * `planSuscripcionApi.ts`), así que mostrarlos ya elegidos sólo adelanta en pantalla lo que el
 * backend haría de todos modos — no es una decisión nueva.
 */
export function valoresPorDefecto(efectivos: PlanSuscripcionVigente): EditablesPlan {
  if (efectivos.origen === 'BD') {
    return {
      montoCentimos: efectivos.montoCentimos,
      intervalo: efectivos.intervalo,
      moneda: efectivos.moneda,
      nombre: efectivos.nombre,
    };
  }
  return {
    montoCentimos: Number.NaN,
    intervalo: 'MENSUAL',
    moneda: MONEDA_POR_DEFECTO,
    nombre: NOMBRE_POR_DEFECTO,
  };
}

/** Lo que gobierna el botón de publicar y el texto que lo acompaña. */
export interface EstadoPublicacionPlan {
  /** Algún campo del formulario difiere de lo que rige ahora. `false` sin versión vigente: no hay con qué comparar. */
  hayCambios: boolean;
  /** El monto es un entero positivo, el intervalo es uno de los cuatro, y moneda/nombre no quedaron vacíos. */
  valido: boolean;
  /** No hay ningún plan publicado — no hay `PlanSuscripcionVigente` con `origen: 'BD'`. */
  sinVersionVigente: boolean;
  puedePublicar: boolean;
  textoBoton: string;
  /** Aclaración al lado del botón, o `null` si no hay nada que aclarar. */
  ayuda: string | null;
}

/**
 * Resuelve en un solo lugar si se puede publicar y qué se le dice al operador.
 *
 * 🔴 **`hayCambios` NO puede ser el único criterio — mismo motivo que en `Dispatch.tsx`, y el caso
 * que lo rompe es la instalación recién construida.** Sin plan publicado, `valoresPorDefecto`
 * arranca el formulario con `montoCentimos: NaN`, así que NINGÚN valor coincide con "lo vigente"
 * (no hay lo vigente) — pero comparar contra `undefined` daría `hayCambios: false` la primera vez
 * que el operador todavía no tocó nada, y el botón quedaría deshabilitado exactamente cuando la
 * única acción posible es publicar el primer plan. `sinVersionVigente` se suma a `hayCambios` como
 * motivo independiente, igual que en dispatch y en precio.
 */
export function calcularEstadoPublicacionPlan(entrada: {
  valores: EditablesPlan;
  efectivos: PlanSuscripcionVigente;
  proximaVersion: number;
  publicando: boolean;
}): EstadoPublicacionPlan {
  const { valores, efectivos, proximaVersion, publicando } = entrada;

  const sinVersionVigente = efectivos.origen === 'SIN_CONFIGURAR';

  // Sin plan vigente no hay con qué comparar — `sinVersionVigente` ya cubre el motivo de publicar.
  const hayCambios =
    efectivos.origen === 'BD' &&
    (valores.montoCentimos !== efectivos.montoCentimos ||
      valores.intervalo !== efectivos.intervalo ||
      valores.moneda !== efectivos.moneda ||
      valores.nombre !== efectivos.nombre);

  // Espeja lo que exige el backend: entero positivo (`@IsInt() @Min(1)` sobre `montoCentimos`),
  // uno de los cuatro intervalos, y moneda/nombre no vacíos — el `<select>` nunca produce un
  // valor fuera de la lista, pero moneda/nombre son `<input>` de texto libre que el operador
  // puede vaciar.
  const valido =
    Number.isInteger(valores.montoCentimos) &&
    valores.montoCentimos >= 1 &&
    INTERVALOS_TODOS.includes(valores.intervalo) &&
    valores.moneda.trim().length > 0 &&
    valores.nombre.trim().length > 0;

  const puedePublicar = (hayCambios || sinVersionVigente) && valido && !publicando;

  const textoBoton = publicando
    ? 'Guardando…'
    : sinVersionVigente
      ? `Publicar como versión ${proximaVersion}`
      : `Guardar como versión ${proximaVersion}`;

  const ayuda = !valido
    ? 'El monto tiene que ser un número entero de 1 o más, y la moneda y el nombre no pueden quedar vacíos.'
    : hayCambios
      ? null
      : sinVersionVigente
        ? `No hay ningún plan publicado: publicar deja estos datos guardados como la versión ${proximaVersion}, y desde ahí un conductor puede iniciar su suscripción.`
        : 'Sin cambios que guardar.';

  return { hayCambios, valido, sinVersionVigente, puedePublicar, textoBoton, ayuda };
}

export function PlanSuscripcion() {
  const [carga, recargar] = useCargaReal(() => obtenerPlanSuscripcion(), []);

  const [borrador, setBorrador] = useState<EditablesPlan | null>(null);
  const [confirmando, setConfirmando] = useState(false);
  const [publicando, setPublicando] = useState(false);
  const [errorAccion, setErrorAccion] = useState<string | null>(null);

  if (carga.estado === 'cargando') {
    return (
      <Pagina rotulo="Solo administrador" titulo="Plan de suscripción">
        <div className="bloque">
          <Cargando filas={4} etiqueta="Cargando el plan de suscripción" />
        </div>
      </Pagina>
    );
  }

  if (carga.estado === 'error') {
    return (
      <Pagina rotulo="Solo administrador" titulo="Plan de suscripción">
        <div className="bloque">
          <ConError detalle={carga.mensaje} alReintentar={recargar} />
        </div>
      </Pagina>
    );
  }

  const { efectivos, historial } = carga.datos;

  /**
   * La versión que está VIGENTE en la tabla, o `undefined` si no hay ninguna.
   *
   * 🔴 **No es `historial[0]`.** El historial puede estar vacío, o traer sólo versiones cerradas
   * — y en los dos casos `historial[0]` mentiría diciendo que hay una vigente. `vigenteHasta ===
   * null` es el mismo criterio que usa el backend para cerrarla, así que las dos puntas dicen lo
   * mismo.
   */
  const vigente = historial.find((version) => version.vigenteHasta === null);

  /** Espeja `(anterior?.version ?? 0) + 1` del backend. Sin fila vigente la próxima es la 1. */
  const proximaVersion = vigente ? vigente.version + 1 : 1;

  const valores: EditablesPlan = borrador ?? valoresPorDefecto(efectivos);

  const { hayCambios, sinVersionVigente, puedePublicar, textoBoton, ayuda } =
    calcularEstadoPublicacionPlan({
      valores,
      efectivos,
      proximaVersion,
      publicando,
    });

  async function publicar(motivo: string): Promise<void> {
    setPublicando(true);
    setErrorAccion(null);
    const cuerpo: PublicarPlanSuscripcion = {
      montoCentimos: valores.montoCentimos,
      intervalo: valores.intervalo,
      moneda: valores.moneda,
      nombre: valores.nombre,
      motivo,
    };
    try {
      await publicarPlanSuscripcion(cuerpo);
      setConfirmando(false);
      setBorrador(null);
      recargar();
    } catch (error) {
      setConfirmando(false);
      setErrorAccion(mensajeDeFalla(error));
      // 🔴 El borrador NO se descarta cuando falla. Si la publicación se rechazó (un 409 porque
      // otro operador publicó en simultáneo, un 400), tirar lo que el operador acababa de
      // escribir lo obliga a rehacerlo de memoria. Se recarga el estado para que compare contra
      // lo que quedó vigente de verdad, y el borrador sigue en pantalla.
      recargar();
    } finally {
      setPublicando(false);
    }
  }

  return (
    <Pagina
      rotulo="Solo administrador"
      titulo="Plan de suscripción"
      ayuda="Cuánto se le cobra a un conductor cada mes para operar. Cada cambio crea una versión nueva con tu nombre y el motivo, así después se puede correlacionar un cambio de monto con lo que pasó con las suscripciones."
    >
      {errorAccion ? (
        <div className="bloque" style={{ marginBottom: 0 }}>
          <ConError titulo="No se pudo publicar la versión" detalle={errorAccion} />
        </div>
      ) : null}

      {/*
        🔴 `origen === 'SIN_CONFIGURAR'` significa que NINGÚN PLAN GOBIERNA EL COBRO — no es un
        error de red, es el estado esperable de una instalación que todavía no publicó nada. A
        diferencia de dispatch/precio, acá no hay un monto de arranque que mostrar: mientras dure,
        el alta de una suscripción nueva falla del lado del backend (503), así que ningún
        conductor puede empezar a pagar.
      */}
      {efectivos.origen === 'SIN_CONFIGURAR' ? (
        <div className="bloque" style={{ marginBottom: 0 }}>
          <ConError
            titulo="Todavía no hay ningún plan publicado"
            detalle="Mientras tanto, ningún conductor puede iniciar el cobro mensual. Completá el formulario de abajo y publicá uno para habilitarlo."
            alReintentar={recargar}
          />
        </div>
      ) : null}

      <Bloque
        titulo={vigente ? `Versión ${vigente.version}, vigente` : 'Sin plan publicado'}
        nota={
          vigente
            ? `Desde ${laFechaHora(vigente.vigenteDesde)} · la puso ${vigente.editadoPor ?? 'sin registrar'}`
            : 'Todavía no hay ningún monto guardado. Completá los datos y publicá la primera versión.'
        }
      >
        <div
          className="rejilla"
          style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))' }}
        >
          <label className="campo">
            <span className="campo__etiqueta rotulo">
              Monto mensual <span className="apagado">· céntimos</span>
            </span>
            <input
              className="entrada"
              type="number"
              min={1}
              step={1}
              value={Number.isNaN(valores.montoCentimos) ? '' : valores.montoCentimos}
              onChange={(evento) =>
                setBorrador({ ...valores, montoCentimos: Number(evento.target.value) })
              }
            />
            <span className="campo__ayuda">
              {Number.isNaN(valores.montoCentimos)
                ? 'El conductor piensa en su moneda, no en céntimos: acá se ve el equivalente en vivo.'
                : `Equivale a ${formatoMonto(valores.montoCentimos, valores.moneda)}.`}
            </span>
            {efectivos.origen === 'BD' && valores.montoCentimos !== efectivos.montoCentimos ? (
              <span className="campo__ayuda" style={{ color: 'var(--ambar)' }}>
                Ahora está en {formatoMonto(efectivos.montoCentimos, efectivos.moneda)}.
              </span>
            ) : null}
          </label>

          <label className="campo">
            <span className="campo__etiqueta rotulo">Intervalo de cobro</span>
            <select
              className="selector"
              value={valores.intervalo}
              onChange={(evento) =>
                setBorrador({ ...valores, intervalo: evento.target.value as IntervaloSuscripcion })
              }
            >
              {INTERVALOS_TODOS.map((intervalo) => (
                <option key={intervalo} value={intervalo}>
                  {ETIQUETA_INTERVALO[intervalo]}
                </option>
              ))}
            </select>
            <span className="campo__ayuda">Cada cuánto se le vuelve a cobrar al conductor.</span>
            {efectivos.origen === 'BD' && valores.intervalo !== efectivos.intervalo ? (
              <span className="campo__ayuda" style={{ color: 'var(--ambar)' }}>
                Ahora está en {ETIQUETA_INTERVALO[efectivos.intervalo]}.
              </span>
            ) : null}
          </label>

          <label className="campo">
            <span className="campo__etiqueta rotulo">Moneda</span>
            <input
              className="entrada"
              type="text"
              value={valores.moneda}
              onChange={(evento) => setBorrador({ ...valores, moneda: evento.target.value })}
            />
            <span className="campo__ayuda">Código ISO de 3 letras, p. ej. PEN.</span>
            {efectivos.origen === 'BD' && valores.moneda !== efectivos.moneda ? (
              <span className="campo__ayuda" style={{ color: 'var(--ambar)' }}>
                Ahora está en {efectivos.moneda}.
              </span>
            ) : null}
          </label>

          <label className="campo">
            <span className="campo__etiqueta rotulo">Nombre del plan</span>
            <input
              className="entrada"
              type="text"
              value={valores.nombre}
              onChange={(evento) => setBorrador({ ...valores, nombre: evento.target.value })}
            />
            <span className="campo__ayuda">Cómo se identifica esta versión en la auditoría.</span>
            {efectivos.origen === 'BD' && valores.nombre !== efectivos.nombre ? (
              <span className="campo__ayuda" style={{ color: 'var(--ambar)' }}>
                Ahora está en {efectivos.nombre}.
              </span>
            ) : null}
          </label>
        </div>

        <div className="fila" style={{ marginTop: 6 }}>
          <button
            type="button"
            className="boton boton--principal"
            disabled={!puedePublicar}
            onClick={() => setConfirmando(true)}
          >
            {textoBoton}
          </button>
          <button
            type="button"
            className="boton"
            disabled={!hayCambios || publicando}
            onClick={() => setBorrador(null)}
          >
            Descartar cambios
          </button>
          {ayuda ? <span className="apagado">{ayuda}</span> : null}
        </div>
      </Bloque>

      <Bloque
        titulo="Historial"
        nota="Qué cambió, quién y por qué. Es lo que permite mirar un cambio de monto contra lo que pasó con las suscripciones después."
        sinRelleno
      >
        {historial.length === 0 ? (
          <div className="bloque__cuerpo">
            <Vacio
              titulo="Todavía no hay ninguna versión guardada"
              detalle="La primera que publiques va a aparecer acá, con tu nombre y el motivo."
            />
          </div>
        ) : (
          <Tabla
            cabeceras={['Versión', 'Desde', 'Monto', 'Intervalo', 'Moneda', 'Quién', 'Por qué']}
          >
            {historial.map((version) => (
              <tr
                key={version.id}
                data-estado={version.vigenteHasta === null ? 'bueno' : undefined}
              >
                <td>
                  {version.version}
                  {version.vigenteHasta === null ? <div className="apagado">vigente</div> : null}
                </td>
                <td className="tenue">{laFechaHora(version.vigenteDesde)}</td>
                <td>{formatoMonto(version.montoCentimos, version.moneda)}</td>
                <td>{ETIQUETA_INTERVALO[version.intervalo]}</td>
                <td>{version.moneda}</td>
                {/*
                  `editadoPor`/`motivo` pueden ser `null`. NO se rellenan con un centinela: la
                  columna no distingue "nació con el sistema" de "alguien la escribió por fuera y
                  no quedó registrado", y ponerlo inventaría una atribución sobre una versión que
                  decide cuánto se le cobra a un conductor cada mes.
                */}
                <td>{version.editadoPor ?? <span className="apagado">sin registrar</span>}</td>
                <td style={{ fontFamily: 'var(--sans)', maxWidth: 340 }}>
                  {version.motivo ?? <span className="apagado">sin motivo registrado</span>}
                </td>
              </tr>
            ))}
          </Tabla>
        )}
      </Bloque>

      {confirmando ? (
        <DialogoMotivo
          titulo={
            sinVersionVigente
              ? `Publicar la versión ${proximaVersion}`
              : `Guardar la versión ${proximaVersion}`
          }
          descripcion={
            // Misma distinción que el botón (ver `calcularEstadoPublicacionPlan`): sin plan
            // vigente hoy NADIE puede suscribirse, así que decir "esto cambia cuánto se cobra"
            // sería falso — lo que cambia es que empieza a haber un cobro posible.
            sinVersionVigente
              ? 'Hoy no hay ningún plan publicado: ningún conductor puede iniciar su cobro mensual. Publicar estos datos los deja guardados como la versión 1, y desde ahí la suscripción queda habilitada. Escribí por qué es este monto: es lo que se va a leer al revisar las suscripciones de la semana. Mínimo 10 caracteres — lo exige el backend.'
              : 'Esto cambia cuánto se le cobra a un conductor desde el momento en que se guarda. Escribí qué esperás que pase: es lo que se va a leer al revisar las suscripciones de la semana. Mínimo 10 caracteres — lo exige el backend.'
          }
          etiquetaConfirmar={sinVersionVigente ? 'Publicar versión' : 'Guardar versión'}
          alConfirmar={(motivo) => void publicar(motivo)}
          alCancelar={() => setConfirmando(false)}
        />
      ) : null}
    </Pagina>
  );
}
